
import React from 'react';
import { Card } from "@/components/ui/card";
import { BarChart2, Info } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { PerformanceData } from './types';
import { useIsMobile } from '@/hooks/use-mobile';
import ResponsiveChartContainer from './ResponsiveChartContainer';
import ResponsiveChartTooltip from './ResponsiveChartTooltip';

interface SubjectComparisonChartProps {
  performanceData: PerformanceData[];
}

export const SubjectComparisonChart: React.FC<SubjectComparisonChartProps> = ({ performanceData }) => {
  const { isMobile, screenSize } = useIsMobile();
  
  const subjectData = React.useMemo(() => {
    const subjectStats = performanceData.reduce((acc, curr) => {
      if (!acc[curr.subject]) {
        acc[curr.subject] = { correct: 0, total: 0 };
      }
      acc[curr.subject].correct += curr.correct_answers;
      acc[curr.subject].total += curr.total_questions;
      return acc;
    }, {} as Record<string, { correct: number; total: number }>);

    return Object.entries(subjectStats)
      .map(([subject, stats]) => ({
        subject,
        accuracy: (stats.correct / stats.total) * 100
      }))
      .sort((a, b) => b.accuracy - a.accuracy);
  }, [performanceData]);

  // For mobile, we'll limit the number of subjects shown
  const displayData = isMobile && subjectData.length > 5 
    ? subjectData.slice(0, 5)
    : subjectData;

  return (
    <Card className={`${isMobile ? "p-3" : "p-6"} bg-gradient-to-br from-indigo-50 to-white border border-slate-100 shadow-sm`}>
      <div className="flex justify-between items-center mb-3">
        <h2 className={`${isMobile ? "text-base" : "text-xl"} font-semibold flex items-center gap-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-700 to-indigo-500`}>
          <BarChart2 className={`${isMobile ? "w-4 h-4" : "w-5 h-5"} text-purple-500`} />
          Subject-Wise Performance
        </h2>
        {isMobile && subjectData.length > 5 && (
          <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full flex items-center">
            <Info className="w-3 h-3 mr-0.5" /> 
            Top 5 shown
          </span>
        )}
      </div>

      <ResponsiveChartContainer
        config={{
          margin: isMobile 
            ? { top: 5, right: 5, bottom: 60, left: 0 } 
            : { top: 10, right: 10, bottom: 80, left: 10 }
        }}
        mobileHeight={220}
        desktopHeight={300}
        mobilePadding="px-0"
        desktopPadding="px-0"
      >
        <BarChart data={displayData}>
          <defs>
            <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#9B87F5" stopOpacity={1} />
              <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0.8} />
            </linearGradient>
            <filter id="shadow" height="130%">
              <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#6366F1" floodOpacity="0.2"/>
            </filter>
          </defs>
          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
          <XAxis 
            dataKey="subject" 
            tick={{ fill: '#6B7280', fontSize: isMobile ? (screenSize === 'xs' ? 8 : 10) : 12 }}
            tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }}
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
          />
          <YAxis 
            tick={{ fill: '#6B7280', fontSize: isMobile ? (screenSize === 'xs' ? 8 : 10) : 12 }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickLine={{ stroke: '#E5E7EB' }}
            width={isMobile ? (screenSize === 'xs' ? 25 : 30) : 40}
            tickFormatter={(value) => `${value}%`}
          />
          <ResponsiveChartTooltip 
            formatter={(value: number) => [`${value.toFixed(1)}%`, 'Score']}
            contentStyle={{ 
              backgroundColor: 'white', 
              border: '1px solid #E5E7EB', 
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}
          />
          <Bar 
            dataKey="accuracy" 
            fill="url(#barGradient)"
            radius={[4, 4, 0, 0]}
            filter="url(#shadow)"
            animationDuration={1500}
            animationEasing="ease-out"
          />
        </BarChart>
      </ResponsiveChartContainer>
    </Card>
  );
};
